import React, { useRef } from 'react';
import 'twin.macro';
import { ArrowRightIcon as ArrowRight } from '@heroicons/react/20/solid';
import { TrashIcon as Trash } from '@heroicons/react/24/outline';
import Button from '@teetsh/app/src/components/Buttons/Button';
import Editor from '@teetsh/app/src/components/Editor/Loadable';
import AutoSuggestGroup from '@teetsh/app/src/components/Form/AutoSuggestGroup';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { Item } from '@teetsh/app/src/types/user';
import { SelectCustomField } from 'components/NewCustomFields/SelectCustomField';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { Trans } from 'react-i18next';
import ButtonGroup from 'components/Form/ButtonGroup';
import {
  CustomFieldDefinition,
  CustomFieldDefinitionDefaults,
  CustomFieldOption,
  CustomFieldValue,
} from 'types/customField';
import { EditCustomFieldDefinition } from 'containers/CahierJournal/EventModal/Sidebar/EditCustomFieldDefinition';

interface Props {
  customField?: CustomFieldValue;
  customFieldDefinition: CustomFieldDefinition;
  onChange: (v: CustomFieldValue) => void;
  onDelete: () => void;
  onUpdateDefinition: (v: CustomFieldDefinition) => Promise<void>;
  onDeleteDefinition: (v: CustomFieldDefinition) => Promise<void>;
  inputRef?: any;
  alignText?: 'center' | 'right' | 'left';
  enableEdit: boolean;
}

export function CustomFieldComponent(props: Props) {
  const {
    customField,
    customFieldDefinition,
    onDelete,
    onChange,
    alignText,
    enableEdit,
    onUpdateDefinition,
    onDeleteDefinition,
  } = props;
  const lastInputRef = useRef<HTMLInputElement>(null);
  const inputRef = props.inputRef ? props.inputRef(lastInputRef) : lastInputRef;

  const isFieldEmpty =
    customField === undefined ||
    CustomFieldDefinitionDefaults[customFieldDefinition.type].isEmpty(
      customField,
    );

  // Le !inputRef, c'est dans le cas du cahier journal, où la création se fait dans la sidebar (=on ne veut pas afficher le bouton)
  // Pour les séquences on aura aussi surement ça, mais plus tard
  if (isFieldEmpty && !props.inputRef) {
    return (
      <ButtonGroup
        key={customFieldDefinition.id}
        className="group"
        tw="flex justify-between"
      >
        <Button
          key={customFieldDefinition.id}
          tw="flex-grow"
          onClick={() => {
            onChange(
              CustomFieldDefinitionDefaults[
                customFieldDefinition.type
              ].genNewItem(customFieldDefinition),
            );
            setTimeout(() => {
              // froala case 😱
              if (inputRef?.current?.editor?.el) {
                inputRef?.current?.editor?.el?.focus();
                return;
              }
              if (inputRef?.current) {
                inputRef?.current?.click();
              }
            }, 100);
          }}
          minimal
          alignText={alignText}
          fill
          aria-label={`Ajouter un champ ${customFieldDefinition.name}`}
          icon={CustomFieldDefinitionDefaults[customFieldDefinition.type].icon}
        >
          {customFieldDefinition.name}
        </Button>
        {enableEdit && (
          //  attention ici ça utilise un composant CJ alors qu'on est dans components !
          <EditCustomFieldDefinition
            customFieldDefinition={customFieldDefinition}
            onUpdateDefinition={onUpdateDefinition}
            onDeleteDefinition={onDeleteDefinition}
          />
        )}
      </ButtonGroup>
    );
  }

  if (isFieldEmpty) {
    return null;
  }

  switch (customFieldDefinition.type) {
    case 'text':
      return (
        <div tw="flex items-end">
          <div tw="flex-grow">
            <FormGroup label={customFieldDefinition.name} withoutMargin>
              <Editor
                inline
                aria-label={customFieldDefinition.name}
                value={(customField.value || '') as string}
                onChange={e => {
                  onChange({
                    ...customField,
                    value: e,
                  });
                }}
                inputRef={inputRef}
              />
            </FormGroup>
          </div>
          <div>
            <Button
              onClick={onDelete}
              intent="danger"
              minimal
              tw="ml-1"
              icon={Trash}
            />
          </div>
        </div>
      );

    case 'select':
      return (
        <div tw="flex items-end">
          <div tw="flex-grow">
            <FormGroup
              id={customFieldDefinition.id}
              label={customFieldDefinition.name}
              withoutMargin
            >
              <SelectCustomField
                id={customFieldDefinition.id}
                options={customFieldDefinition.options.map(v => ({
                  value: v.value,
                  id: v.id,
                  color: v.color,
                }))}
                inputRef={inputRef}
                allowMultipleValue
                value={(customField.value as string[]) || []}
                onChange={newValue =>
                  onChange({
                    ...customField,
                    value: newValue,
                  })
                }
                onUpdateOption={v => {
                  onUpdateDefinition({
                    ...customFieldDefinition,
                    options: customFieldDefinition.options.map(opt =>
                      opt.id === v.id
                        ? { ...opt, value: v.value, color: v.color }
                        : opt,
                    ),
                  });
                }}
                onRemoveOption={optionId => {
                  onUpdateDefinition({
                    ...customFieldDefinition,
                    options: customFieldDefinition.options.filter(
                      opt => opt.id !== optionId,
                    ),
                  });
                }}
                name={customFieldDefinition.name || ''}
                onCreateOption={({ optionValue, color }) => {
                  const optionId = uuid();
                  const newOption: CustomFieldOption = {
                    id: optionId,
                    value: optionValue,
                    color: color || 'framboise-intense',
                    position: 1,
                  };
                  return onUpdateDefinition({
                    ...customFieldDefinition,
                    options: [...customFieldDefinition.options, newOption],
                  }).then(() =>
                    onChange({
                      ...customField,
                      value: [
                        ...((customField.value || []) as string[]),
                        newOption.id,
                      ],
                    }),
                  );
                }}
              />
            </FormGroup>
          </div>
          <div>
            <Button
              onClick={onDelete}
              tw="ml-1"
              intent="danger"
              minimal
              icon={Trash}
            />
          </div>
        </div>
      );

    case 'multiselect':
      return (
        <>
          <AutoSuggestGroup
            inputRef={inputRef}
            items={customField.items || []}
            onChange={(v: Item[]) => {
              if (v.length === 0) {
                onDelete();
                return;
              }
              onChange({ ...customField, items: v });
            }}
            onSave={
              enableEdit
                ? v =>
                    onUpdateDefinition({
                      ...customFieldDefinition,
                      options: [
                        ...customFieldDefinition.options,
                        { id: uuid(), value: v },
                      ],
                    })
                : undefined
            }
            options={customFieldDefinition.options.map(v => ({
              value: v.value,
              label: v.value,
            }))}
            label={
              customFieldDefinition.pluralName || customFieldDefinition.name
            }
          />
          {(customField.value || []).length > 0 && enableEdit && (
            <div tw="mt-1">
              <small>
                <Link target="_blank" to="/settings/custom-fields">
                  <Trans
                    i18nKey="common:components.customFields.accessList"
                    values={{
                      label:
                        customFieldDefinition.pluralName ||
                        `"${customFieldDefinition.name}"`,
                    }}
                  >
                    __A__
                    <i>__B__</i>
                    __C__
                  </Trans>
                  <ArrowRight tw="inline-block h-4 w-4 ml-2" />
                </Link>
              </small>
            </div>
          )}
        </>
      );
    case 'bool':
      return <>TODO</>;
    case 'number':
      return <>TODO</>;
    case 'phone':
      return <>TODO</>;
    case 'filesAndMedia':
      return <>TODO</>;
  }
}
